/**
 * src/components/InviteEmbed.tsx
 *
 * Rich card for a PsydeChat server-invite link. Renders the server's icon,
 * name, description and member count with a one-click JOIN, instead of the
 * bare URL (or the generic Microlink preview) a shared invite used to show.
 *
 * Resolution and redemption both go through SECURITY DEFINER RPCs added in
 * migration 20260918000000:
 *   invite_preview(code) → presentational fields only (never members or
 *                          password_hash), so a non-member can see what
 *                          they're being invited to.
 *   redeem_invite(code)  → validates expiry + max_uses, appends the caller
 *                          to vaults.members, bumps `uses`.
 *
 * Navigation on success is announced via a window CustomEvent rather than a
 * prop callback: this renders inside LinkEmbed, which is itself nested in
 * FeedPostCard / NeuralMessageBubble, and threading a handler up to App
 * through both chains would touch four unrelated components.
 */
import React, { useEffect, useState } from 'react';
import { Users, Check, Plus, Loader2, Globe, Lock, TriangleAlert } from 'lucide-react';
import { supabase } from '../supabase';
import { preloadImage } from '../services/imageCache';
import { retryQuery } from '../utils/retryQuery';

export const INVITE_JOINED_EVENT = 'psydechat:invite-joined';

interface InvitePreview {
  id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  type: string;
  expires_at: string | null;
  max_uses: number;
  uses: number;
  is_member: boolean;
  member_count: number;
  customization?: any;
}

/**
 * Pull the invite code out of a PsydeChat invite URL.
 * Returns null for any URL that isn't one, so callers can fall through to
 * their normal link handling.
 */
export function parseInviteCode(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);
    const code = url.searchParams.get('invite');
    return code && code.trim() ? code.trim() : null;
  } catch {
    return null;
  }
}

export default function InviteEmbed({ code }: { code: string }) {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const { data, error } = await retryQuery(() =>
        supabase.rpc('invite_preview', { p_code: code })
      );
      if (!alive) return;
      if (error) {
        console.error('invite_preview failed:', error.code, error.message);
        setFailure(
          error.code === 'PGRST202'
            ? 'Invites are not set up on this server yet.'
            : 'Could not load this invite.'
        );
      } else if (!data) {
        setFailure('This invite is invalid or has been revoked.');
      } else {
        setPreview(data as InvitePreview);
        // Preload the banner so subsequent renders are instant.
        const bannerUrl = (data as InvitePreview)?.customization?.bannerImage;
        if (bannerUrl) preloadImage(bannerUrl);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [code]);

  const expired = !!preview?.expires_at && new Date(preview.expires_at) < new Date();
  const exhausted = !!preview && preview.max_uses > 0 && preview.uses >= preview.max_uses;
  const unusable = expired || exhausted;

  const handleJoin = async () => {
    if (joining || !preview) return;
    setJoining(true);
    setFailure(null);

    const { data, error } = await supabase.rpc('redeem_invite', { p_code: code });
    setJoining(false);

    if (error) {
      console.error('redeem_invite failed:', error.code, error.message);
      setFailure('Could not join. Try again in a moment.');
      return;
    }

    const result = data as { ok: boolean; reason: string; vault_id?: string } | null;
    if (!result?.ok) {
      setFailure(
        result?.reason === 'expired' ? 'This invite has expired.'
        : result?.reason === 'exhausted' ? 'This invite has reached its use limit.'
        : result?.reason === 'not_found' ? 'This invite is no longer valid.'
        : 'Could not join this server.'
      );
      return;
    }

    setPreview(p => (p ? { ...p, is_member: true, member_count: p.member_count + 1 } : p));
    window.dispatchEvent(
      new CustomEvent(INVITE_JOINED_EVENT, { detail: { vaultId: result.vault_id ?? preview.id } })
    );
  };

  const openServer = () => {
    window.dispatchEvent(
      new CustomEvent(INVITE_JOINED_EVENT, { detail: { vaultId: preview?.id } })
    );
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/40 p-4 flex items-center gap-3 max-w-md">
        <Loader2 size={16} className="animate-spin text-[var(--neon)]" />
        <span className="text-[10px] font-black uppercase tracking-widest text-white/40">
          Resolving invite...
        </span>
      </div>
    );
  }

  if (failure && !preview) {
    return (
      <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-4 flex items-center gap-3 max-w-md">
        <TriangleAlert size={16} className="text-red-400 shrink-0" />
        <span className="text-[11px] text-red-300/80">{failure}</span>
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="rounded-2xl border border-[var(--neon)]/25 bg-gradient-to-br from-[#0b0e1a] to-[#131830] max-w-md shadow-xl relative overflow-hidden group">
      {/* Banner Background */}
      <img
        src={preview.customization?.bannerImage || '/default-server-banner.gif'}
        style={{ objectPosition: preview.customization?.bannerPosition || '50% 50%' }}
        className="absolute inset-0 w-full h-full object-cover opacity-60 -z-10 group-hover:opacity-75 transition-opacity duration-300"
        alt="Banner"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0e1a] via-[#0b0e1a]/60 to-[#0b0e1a]/30 -z-10" />

      <div className="p-4 relative z-10">
        <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[var(--neon)]/70 mb-3 drop-shadow-md">
          You've been invited to join a server
        </div>

      <div className="flex gap-3 items-start">
        <img
          src={preview.icon_url || '/psydechat-icon-no-text.png'}
          alt=""
          className="w-14 h-14 rounded-2xl object-cover border border-white/10 shrink-0 shadow-lg"
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-black text-sm uppercase tracking-tight truncate">
            {preview.name}
          </h3>
          <p className="text-[11px] text-white/50 line-clamp-2 mt-0.5 font-sans leading-relaxed">
            {preview.description || 'No description provided.'}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[9px] font-black uppercase tracking-wider text-white/40">
            <span className="flex items-center gap-1">
              {preview.type === 'public' ? (
                <Globe size={10} className="text-[var(--neon)]" />
              ) : (
                <Lock size={10} className="text-amber-400" />
              )}
              {preview.type === 'public' ? 'Public' : 'Private'}
            </span>
            <span className="flex items-center gap-1">
              <Users size={10} className="text-[var(--neon)]" />
              {preview.member_count} {preview.member_count === 1 ? 'member' : 'members'}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-3">
        <span className="text-[9px] uppercase tracking-widest text-white/25">
          {expired ? 'Invite expired'
            : exhausted ? 'Invite used up'
            : preview.max_uses > 0 ? `${preview.max_uses - preview.uses} uses left`
            : 'Never expires'}
        </span>

        {preview.is_member ? (
          <button
            onClick={openServer}
            className="px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-white/70 font-black text-[10px] uppercase tracking-widest hover:bg-white/15 transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <Check size={13} className="text-emerald-400" /> Open
          </button>
        ) : (
          <button
            onClick={handleJoin}
            disabled={joining || unusable}
            className="px-5 py-2 rounded-xl bg-[var(--neon)] text-black font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-[0_0_15px_var(--neon)] flex items-center gap-1.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {joining ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            {joining ? 'Joining' : 'Join'}
          </button>
        )}
      </div>

      {failure && <p className="mt-2 text-[10px] text-red-400">{failure}</p>}
      </div>
    </div>
  );
}
